import { createLogger } from "@dransay/logger/browser";
import * as Sentry from "@sentry/react-router";

const logger = createLogger({ service: "platform" });

interface GracefulReloadOptions {
	/**
	 * Category for Sentry breadcrumbs (e.g., "network-error", "version-mismatch")
	 */
	category: string;
	/**
	 * Session storage key to track reload attempts
	 */
	storageKey: string;
	/**
	 * Cooldown period in milliseconds to prevent reload loops (default: 10000ms)
	 */
	cooldownMs?: number;
}

/**
 * Triggers a graceful page reload with loop protection.
 * Uses session storage to prevent infinite reload loops by enforcing a cooldown period.
 *
 * @param error - The error that triggered the reload
 * @param options - Configuration options for the reload behavior
 * @returns true if reload was triggered, false if skipped due to cooldown
 */
export function triggerGracefulReload(
	error: Error,
	options: GracefulReloadOptions,
): boolean {
	const { category, storageKey, cooldownMs = 10000 } = options;
	const lastReload = sessionStorage.getItem(storageKey);
	const now = Date.now();

	// Prevent reload loops: only reload if we haven't reloaded within cooldown period
	if (lastReload && now - Number.parseInt(lastReload, 10) < cooldownMs) {
		logger.warn(
			{ category },
			"Skipping reload to prevent loop, error will propagate",
		);
		Sentry.addBreadcrumb({
			category,
			message: "Skipped reload to prevent loop",
			level: "warning",
			data: {
				error: error.message,
				lastReload,
				now,
			},
		});
		return false;
	}

	Sentry.addBreadcrumb({
		category,
		message: "Triggering page reload for recovery",
		level: "info",
		data: {
			error: error.message,
			url: window.location.href,
		},
	});

	logger.info({ category }, "Attempting recovery via page reload");

	// Set the reload flag
	sessionStorage.setItem(storageKey, now.toString());

	// Force a full page reload to recover
	window.location.reload();
	return true;
}
